import { NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, WritableSignal, afterNextRender, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { TEMPLATE_CONSTANT } from '../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { LOCAL_STORAGE_CONSTANTS, PAYMENT_TYPES, WIDGET_CONSTANTS } from '../../../constants/widget-constants';
import { AppointmentServiceDao } from '../../../db-models/appointment-service-dao';
import { CouponDbModel } from '../../../db-models/coupon-db.model';
import { PayPalOrderDetailModel, PayPalSettingsDbModel } from '../../../db-models/partner-setting.model';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { CartItem, EventCartItem, GuestModel } from '../../../models/cart.model';
import { Partner } from '../../../models/global';
import { AdyenPaymentDetails } from '../../../models/adyen.model';
import { AdyenResumeSnapshot } from '../../../models/adyen-resume.model';
import { AssertPaymentPageSaferpayResponse, SaferPayPaymentDetailsDbModel } from '../../../models/saferpay.model';
import { UserState } from '../../../models/state.model';
import { buildAdyenBookingContext, hasAdyenBookingContext } from '../../../services/adyen-booking-context.util';
import { CustomEventService } from '../../../services/custom-event.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { LoggerService } from '../../../services/logger.service';
import { PartnerService } from '../../../services/partner.service';
import { PaymentService } from '../../../services/payment.service';
import { focusAfterPaymentLock } from '../../../services/payment-lock-focus.util';
import { WidgetUtilService } from '../../../services/widget-util.service';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { CalioMeetingTemplatePipe } from '../../../shared/pipes/calio-meeting-template.pipe';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';
import { CouponFormComponent } from '../coupon-form/coupon-form.component';
import { CwInvoiceAddressFormComponent } from '../cw-invoice-address-form/cw-invoice-address-form.component';
import { InvoiceAddress } from '../../../models/invoice-address.model';
import { resolvePreselectedPaymentType, shouldUseCardLayout } from './payment-method-preselection.util';
import { CwPaypalComponent } from '../cw-paypal/cw-paypal.component';
import { CwAdyenComponent } from '../cw-adyen/cw-adyen.component';
import { CwSaferpayComponent } from '../cw-saferpay/cw-saferpay.component';
import { StripeComponent } from '../stripe/stripe.component';
import { CwCardHeaderComponent } from '../theme/cw-card-header/cw-card-header.component';
import { CwNewCardComponent } from '../theme/cw-new-card/cw-new-card.component';

const components = [AlertComponent, CwNewCardComponent, CwCardHeaderComponent, CouponFormComponent, StripeComponent, CwPaypalComponent, CwSaferpayComponent, CwInvoiceAddressFormComponent, CwAdyenComponent];
const pipes = [TranslationPipe, CalioMeetingTemplatePipe];
const modules = [NgClass, NgTemplateOutlet, NgSelectModule, FormsModule, TranslateModule];

@Component({
  selector: 'app-cw-payment-methods',
  templateUrl: './cw-payment-methods.component.html',
  styleUrls: ['./cw-payment-methods.component.scss'],
  imports: [...modules, ...components, ...pipes],
  providers: [TitleCasePipe, TranslationPipe]
})
export class CwPaymentMethodsComponent implements OnInit, OnChanges {

  @ViewChild('paymentTypeErrorRef') private paymentTypeErrorRef: ElementRef<HTMLDivElement>;

  @Input() additionalGuests: GuestModel[] = [];
  @Input() anonymousGuestCount = 0;
  @Input() appointmentServicesAvailable: AppointmentServiceDao[];
  @Input() cart: CartItem[];
  @Input() coupon: CouponDbModel;
  @Input() courtesyForm: string;
  @Input() customCSS = "";
  @Input() eventCart: EventCartItem[];
  @Input() finalCouponDiscount: number;
  @Input() isBexioEnabled = false;
  @Input() isExternalInvoiceEnabled = false;
  @Input() isInvoiceAddressMandatory = false;
  @Input() isLexOfficePaymentTypeEnabled = false;
  @Input() isOnlinePaymentEnabled = false;
  @Input() isOnlinePaymentMandatory = false;
  @Input() isPaypalEnabled = false;
  @Input() isSaferpayEnabled = false;
  @Input() isAdyenEnabled = false;
  /** Selects the merchant account when the partner runs per-branch Adyen accounts. */
  @Input() storeId?: number;
  /** Booking-link mode: the booking goes to the link owner, whose store picks the Adyen account. */
  @Input() bookingLinkUuid?: string;
  /** Pre-defined slots book an existing appointment; its store picks the Adyen account. */
  @Input() predefinedSlotUuid?: string;
  /** `/book`'s own combi flag - shared, never re-derived (see adyen-booking-context.util). */
  @Input() isCombiServiceSelected?: boolean;
  /** True only on a surface whose Book button calls submitAdyen(); see CwAdyenComponent. */
  @Input() hostDrivesSubmit = false;
  /** The host's booking, parked before an Adyen payment leaves the page; see CwAdyenComponent. */
  @Input() adyenResumeSnapshot?: () => AdyenResumeSnapshot | null;
  /**
   * The host's slot reservation has run out: no new Adyen payment may begin, because nothing could
   * book it. The checkout is taken down unless a payment is already under way there (a hosted popup,
   * a 3DS challenge) - its result still has to reach the host, which then books nothing.
   */
  readonly reservationExpired = input(false);
  /** The mounted Drop-in, so Book can drive it (it has no pay button of its own). */
  @ViewChild(CwAdyenComponent) private adyenComponent?: CwAdyenComponent;
  @Input() isStoreEnabled = false;
  @Input() isFormValid = false;
  @Input() isPaymentInProgress = false;
  @Input() isStripeEnabled = false;
  @Input() lang: string;
  @Input() defaultPaymentType = '';
  @Input() partner: Partner;
  @Input() paymentType: string = '';
  @Input() paypalSetting: PayPalSettingsDbModel;
  @Input() customFieldsAddonsPrice = 0;
  @Input() preApplyCoupon: string;
  @Input() priceAfterCouponReduction: number;
  @Input() saferpaySucessfulPaymentEvent: Observable<AssertPaymentPageSaferpayResponse>;
  @Input() stripeConnectedAccountId: string;
  @Input() storageComplete: boolean;
  @Input() totalAmount: number;
  @Input() userState: UserState;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() widgetType: string;

  /** The per-booking Adyen availability answer; false while it is pending. */
  private adyenAvailableForBooking = false;
  /** The booking context the current answer (or pending request) is for. */
  private adyenAvailabilityKey: string | null = null;
  private adyenAvailabilityRequest?: Subscription;

  /**
   * Adyen pays for exactly ONE appointment.
   *
   * The payment log is claimed single-use on the backend, so a cart holding more
   * than one appointment consumes the session on the first and then fails on the
   * second - after the shopper has already been charged. These carts are
   * forbidden rather than given an allocation model, so Adyen is simply not
   * offered for them; the other providers stay available.
   *
   * Events bind `eventCart` instead, and the same holds there: one session pays for
   * one slot, and a second slot's booking would fail the amount check after the
   * money was taken.
   *
   * On top of that, Adyen must be available for THIS booking's store. The
   * partner-wide flag only says some store has an account; offering Adyen before
   * the per-booking answer is in would mint a session the backend then refuses.
   */
  protected get isAdyenSelectable(): boolean {
    return this.isOnlinePaymentEnabled
      && this.isAdyenEnabled
      && (this.cart?.length ?? 0) <= 1
      && (this.eventCart?.length ?? 0) <= 1
      && this.adyenAvailableForBooking;
  }

  @Output() couponSuccessEvent = new EventEmitter<string>();
  @Output() couponFailedEvent = new EventEmitter<{
    disableBookingButton: boolean,
    invalidCoupon: boolean,
  }>();
  @Output() changePaymentTypeEvent = new EventEmitter<string>();
  @Output() paypalSuccessfulEvent = new EventEmitter<PayPalOrderDetailModel>();
  @Output() resetPaypalEvent = new EventEmitter<any>();
  @Output() paypalOrderStartedEvent = new EventEmitter<void>();
  @Output() paypalCancelledEvent = new EventEmitter<void>();
  @Output() cancelPaymentEvent = new EventEmitter<void>();
  @Output() saferpaySuccessfulEvent = new EventEmitter<SaferPayPaymentDetailsDbModel>();
  @Output() resetSaferpayEvent = new EventEmitter<string | void>();
  @Output() adyenSuccessfulEvent = new EventEmitter<AdyenPaymentDetails>();
  @Output() resetAdyenEvent = new EventEmitter<string | void>();
  /** Drop-in really started a payment (its beforeSubmit) - not merely that we asked it to. */
  @Output() adyenPaymentStartedEvent = new EventEmitter<void>();
  /** The customer pressed "Pay again" after an unknown outcome or a spent session; the form is re-armed. */
  @Output() payAgainEvent = new EventEmitter<void>();
  @Output() stripeSuccessfulEvent = new EventEmitter<string>();
  @Output() resetStripeTokenEvent = new EventEmitter<void>();
  @Output() stripElementLoadEvent = new EventEmitter<boolean>();
  @Output() storeBookingItemsEvent = new EventEmitter<void>();
  @Output() invoiceAddressChangeEvent = new EventEmitter<{ checked: boolean, valid: boolean, value: InvoiceAddress | null }>();

  private customEventService = inject(CustomEventService);
  private destroyRef = inject(DestroyRef);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private injector = inject(Injector);
  private localStorageService = inject(LocalStorageService);
  private partnerService = inject(PartnerService);
  private paymentService = inject(PaymentService);
  private titlecasePipe = inject(TitleCasePipe);
  private translate = inject(TranslateService);
  private translationPipe = inject(TranslationPipe);
  private widgetUtilService = inject(WidgetUtilService);
  
  protected readonly paymentTypes = PAYMENT_TYPES;
  protected readonly templateConstant = TEMPLATE_CONSTANT;
  protected readonly templateContent = TRANSLATION_TEMPLATES;
  protected readonly widgetConstants = WIDGET_CONSTANTS;
  protected hasValidCoupons = 0;
  protected hidePaymentOptions = false;
  protected inStoreTemplate: CustomBookingMessageTemplate;
  protected paymentFailedMsg: string;
  protected paymentSuccessMsg: string;
  protected paypalTemplate: CustomBookingMessageTemplate;
  protected paymentOptions: { value: string; label: string }[] = [];
  protected price: number;
  protected saferpayTemplate: CustomBookingMessageTemplate;
  protected showAllPaymentOptions = true;
  protected useCardLayout = false;
  private hasExpandedPaymentOptions = false;
  private hasCustomerPickedPaymentType = false;
  protected showCouponField = false;
  protected showPaymentFailedMsg = false;
  protected showPaymentSuccessMsg = false;
  protected showPaymentTypeError = false;
  /**
   * Drop-in has started a payment (its beforeSubmit) and has not reported a result yet.
   *
   * Owned here rather than by each host because all four surfaces render Drop-in through this
   * component, and it is the only place that sees both the start and every outcome. It freezes
   * only what changes the amount or the method - the coupon and the method picker - and is NOT
   * `isPaymentInProgress`: that flag also goes up for PayPal, whose inline card fields must stay
   * usable (the form-wide lock was removed for exactly that reason).
   */
  protected readonly adyenPaymentInFlight = signal(false);
  /**
   * A paid /book lost its answer, so the booking may exist. The payment form stays down until the
   * customer presses "Pay again" - re-arming it on its own invited a second payment for a booking
   * that had already gone through.
   */
  protected readonly showBookingOutcomeUnknown = signal(false);
  /**
   * The backend will not claim the payment session again. A booking may or may not stand behind
   * it, so this is handled like a lost answer: check the email first, then "Pay again".
   */
  protected readonly showPaymentSessionSpent = signal(false);
  /**
   * A paid /book was still answered "booking lock" after every resend: the booking is most likely
   * being completed by this payment's own first request. Check the email - no "Pay again".
   */
  protected readonly showBookingStillCompleting = signal(false);
  protected templateSubTitle: CustomBookingMessageTemplate;
  protected templateTitle: CustomBookingMessageTemplate;
  protected stripeTemplate: CustomBookingMessageTemplate;

  constructor() {
    this.customEventService.paymentGatewayErrorEvent.subscribe(result => {
      this.showPaymentTypeError = result;
      if (result) {
        // Wait for the alert to be rendered before scrolling
        setTimeout(() => this.scrollToPaymentTypeError());
      }
    });
    this.translate.onLangChange.subscribe(lang => this.lang = lang.lang);
  }

  private scrollToPaymentTypeError(): void {
    this.paymentTypeErrorRef?.nativeElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }

  ngOnInit(): void {
    this.hasValidCoupons = this.widgetUtilService.getWidgetConf()?.has_valid_coupons;
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(lang => {
      this.lang = lang.lang;
      this.buildPaymentOptions();
    });
    // On /book error after PayPal/Saferpay succeeded, the parent fires this so
    // the success banner clears and the payment widgets come back into the DOM
    // (the @if (!hidePaymentOptions) guard above the cw-paypal / cw-saferpay
    // blocks) — the customer can re-authorize a fresh payment for the retry.
    this.paymentService.resetPaymentSuccessStateSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rearmPaymentOptions());
    this.paymentService.bookingOutcomeUnknownSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.holdForPayAgain(this.showBookingOutcomeUnknown));
    this.paymentService.paymentSessionSpentSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.holdForPayAgain(this.showPaymentSessionSpent));
    this.paymentService.bookingStillCompletingSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.holdForPayAgain(this.showBookingStillCompleting));
    // The booking exists: the payment form stays down with no notice of its own and no "Pay again" -
    // the host's "already booked" notice beside it says what happened.
    this.paymentService.paymentAlreadyBookedSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.holdForPayAgain());
    // Book must not stay disabled on a view this component no longer backs.
    this.destroyRef.onDestroy(() => this.paymentService.payAgainRequired.set(false));
    this.customCSS += (this.widgetType === this.widgetConstants.COMPACT ? ' compact-payment-method' : '');

    this.priceAfterCouponReduction = this.totalAmount;
    this.calculatePrice();
    // For an event, totalAmount is the net price of one person; the real total (guests, coupon,
    // VAT added on top) was announced before this view existed.
    const lastEventTotal = this.customEventService.lastAdditionalGuestChange;
    if (this.widgetType === this.widgetConstants.EVENT && lastEventTotal) {
      this.price = lastEventTotal.finalPrice;
    }
    this.buildPaymentOptions();

    this.customEventService.additionalGuestChangeEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (this.widgetType === this.widgetConstants.EVENT) {
          this.price = result.finalPrice;
          this.refreshAdyenAvailability();
        }
      });

    // The cart can change in place (the parent keeps the same array), which fires no ngOnChanges -
    // but it changes which booking an Adyen session would be for.
    this.customEventService.cartItemsChangeEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshAdyenAvailability());

    this.refreshAdyenAvailability();
    this.handlePaymentTypePredefinedValue();
    if (this.preApplyCoupon) {
      this.showCouponField = true;
    }
  }

  handlePaymentTypePredefinedValue(): void {
    const enabledMethods = this.getEnabledPaymentMethods();
    this.useCardLayout = shouldUseCardLayout(this.defaultPaymentType, enabledMethods);

    // A method the customer picked themselves wins over any pre-selection —
    // re-running this must never revert their choice.
    if (this.hasCustomerPickedPaymentType && enabledMethods.includes(this.paymentType)) {
      return;
    }

    this.localStorageService.getItem(LOCAL_STORAGE_CONSTANTS.LAST_PAYMENT_METHOD, (lastPaymentType: null | undefined | string) => {
      // Recompute inside the callback: the lookup is async, so the flags may have
      // moved on since the snapshot taken above for the customer-pick guard. A stored
      // choice is only reapplied while that method is still enabled — the util checks it
      // against enabledMethods, which carries external_invoice only while the partner has
      // the mode on.
      if (this.hasCustomerPickedPaymentType && this.getEnabledPaymentMethods().includes(this.paymentType)) {
        return;
      }

      const preselectedPaymentType = resolvePreselectedPaymentType(
        lastPaymentType,
        this.defaultPaymentType,
        this.getEnabledPaymentMethods(),
        this.isStoreEnabled
      );

      if (!preselectedPaymentType) {
        // Nothing to pre-select: drop a stale selection and show every option,
        // so the card list can never end up filtered down to nothing.
        this.paymentType = '';
        this.showAllPaymentOptions = true;
        return;
      }

      this.onChangePaymentType(preselectedPaymentType);
      // Collapse to the pre-selected card; the customer expands the rest on demand.
      // A list the customer expanded themselves stays expanded.
      if (!this.hasExpandedPaymentOptions) {
        this.showAllPaymentOptions = this.paymentOptions.length <= 1;
      }
    });
  }

  /**
   * Returns all currently enabled payment methods based on partner configuration.
   */
  private getEnabledPaymentMethods(): string[] {
    const methods: string[] = [];

    if (this.isOnlinePaymentEnabled && this.isStripeEnabled) {
      methods.push(this.paymentTypes.CREDITCARD);
    }
    if (this.isBexioEnabled) {
      methods.push(this.paymentTypes.INVOICE);
    }
    if (this.isExternalInvoiceEnabled) {
      methods.push(this.paymentTypes.EXTERNAL_INVOICE);
    }
    if (this.isOnlinePaymentEnabled && this.isPaypalEnabled) {
      methods.push(this.paymentTypes.PAYPAL);
    }
    if (this.isStoreEnabled) {
      methods.push(this.paymentTypes.STORE);
    }
    if (this.isOnlinePaymentEnabled && this.isSaferpayEnabled) {
      methods.push(this.paymentTypes.SAFERPAY);
    }
    if (this.isAdyenSelectable) {
      methods.push(this.paymentTypes.ADYEN);
    }
    if (this.isLexOfficePaymentTypeEnabled) {
      methods.push(this.paymentTypes.LEX_OFFICE_INVOICE);
    }

    return methods;
  }

  /**
   * Whether a payment method card should stay hidden while the list is
   * collapsed to the pre-selected option. The cards are always rendered
   * (never added/removed from the DOM) so a live provider iframe nested in
   * one of them is never re-parented and reloaded.
   */
  protected isPaymentOptionHidden(value: string): boolean {
    if (this.showAllPaymentOptions) {
      return false;
    }
    // A selection that is no longer on offer would collapse the list to
    // nothing — show everything instead of an empty payment step.
    const hasSelectedOption = this.paymentOptions.some(option => option.value === this.paymentType);
    return hasSelectedOption && this.paymentType !== value;
  }

  protected displayAllPaymentOptions(): void {
    this.showAllPaymentOptions = true;
    this.hasExpandedPaymentOptions = true;
  }

  /**
   * Builds the payment options array based on enabled payment methods.
   * This ensures ng-select always has a stable array to iterate over.
   */
  private buildPaymentOptions(): void {
    const options: { value: string; label: string }[] = [];

    if (this.isOnlinePaymentEnabled && this.isStripeEnabled) {
      options.push({
        value: this.paymentTypes.CREDITCARD,
        label: this.getTemplateLabel(this.stripeTemplate) || this.translate.instant('summary_page_translations.creditcard')
      });
    }
    if (this.isBexioEnabled) {
      options.push({
        value: this.paymentTypes.INVOICE,
        label: this.translate.instant('summary_page_translations.invoice')
      });
    }
    // Own label rather than reusing `invoice`: that one has meant Bexio on every environment
    // for years, so reusing it would silently redefine a term customers and partners already
    // know. Same approach the lexOffice option takes with `lexoffice`.
    if (this.isExternalInvoiceEnabled) {
      options.push({
        value: this.paymentTypes.EXTERNAL_INVOICE,
        label: this.translate.instant('summary_page_translations.external_invoice')
      });
    }
    if (this.isOnlinePaymentEnabled && this.isPaypalEnabled) {
      options.push({
        value: this.paymentTypes.PAYPAL,
        label: this.getTemplateLabel(this.paypalTemplate) || this.translate.instant('summary_page_translations.paypal')
      });
    }
    if (this.isStoreEnabled) {
      options.push({
        value: this.paymentTypes.STORE,
        label: this.getTemplateLabel(this.inStoreTemplate) || this.translate.instant('summary_page_translations.in_store')
      });
    }
    if (this.isOnlinePaymentEnabled && this.isSaferpayEnabled) {
      options.push({
        value: this.paymentTypes.SAFERPAY,
        label: this.getTemplateLabel(this.saferpayTemplate) || 'Saferpay'
      });
    }
    if (this.isAdyenSelectable) {
      options.push({
        value: this.paymentTypes.ADYEN,
        label: this.translate.instant('adyen.payment_method_label')
      });
    }
    if (this.isLexOfficePaymentTypeEnabled) {
      options.push({
        value: this.paymentTypes.LEX_OFFICE_INVOICE,
        label: this.translate.instant('summary_page_translations.lexoffice')
      });
    }

    this.paymentOptions = options;
  }

  /**
   * Get translated label from template using TranslationPipe
   */
  private getTemplateLabel(template: CustomBookingMessageTemplate): string {
    if (!template) return '';
    return this.translationPipe.transform(template, this.templateContent.TEMPLATE_CONTENT, this.lang) || '';
  }

  calculatePrice(): void {
    if (this.priceAfterCouponReduction || this.priceAfterCouponReduction === 0) {
      this.price = this.priceAfterCouponReduction;
    } else {
      this.price = this.totalAmount;
    }
  }

  displayCouponField(): void {
    this.showCouponField = !this.showCouponField;
  }

  ngOnChanges(changes: SimpleChanges): void {
    let checkForPaymentChange = false;
    // An invalid form gets the form-validation message; a payment message from an earlier press
    // would otherwise stand beside it, and come back unasked once the form is valid again.
    if (changes?.isFormValid && !this.isFormValid) {
      this.showPaymentTypeError = false;
    }
    if (this.widgetType !== this.widgetConstants.EVENT) {
      this.calculatePrice();
    }

    if (changes?.widgetTemplates?.currentValue) {
      if (this.widgetTemplates?.length) {
        this.setupTemplates();
        this.buildPaymentOptions();
      }
    }

    if (changes && changes?.isStripeEnabled !== undefined) {
      this.isStripeEnabled = changes.isStripeEnabled.currentValue;
      checkForPaymentChange = true;
    }

    if (changes && changes?.isBexioEnabled !== undefined) {
      this.isBexioEnabled = changes.isBexioEnabled.currentValue;
      checkForPaymentChange = true;
    }

    if (changes && changes?.isExternalInvoiceEnabled !== undefined) {
      this.isExternalInvoiceEnabled = changes.isExternalInvoiceEnabled.currentValue;
      checkForPaymentChange = true;
    }

    if (changes && changes?.isSaferpayEnabled !== undefined) {
      this.isSaferpayEnabled = changes.isSaferpayEnabled.currentValue;
      checkForPaymentChange = true;
    }

    if (changes && changes?.isAdyenEnabled !== undefined) {
      this.isAdyenEnabled = changes.isAdyenEnabled.currentValue;
      checkForPaymentChange = true;
    }

    if (changes && changes?.isPaypalEnabled !== undefined) {
      this.isPaypalEnabled = changes.isPaypalEnabled.currentValue;
      checkForPaymentChange = true;
    }

    if (changes && changes?.isStoreEnabled !== undefined) {
      this.isStoreEnabled = changes.isStoreEnabled.currentValue;
      checkForPaymentChange = true;
    }

    if (changes && changes?.isLexOfficePaymentTypeEnabled !== undefined) {
      this.isLexOfficePaymentTypeEnabled = changes.isLexOfficePaymentTypeEnabled.currentValue;
      checkForPaymentChange = true;
    }

    if (changes && changes?.defaultPaymentType !== undefined) {
      this.defaultPaymentType = changes.defaultPaymentType.currentValue;
      checkForPaymentChange = true;
    }

    if (checkForPaymentChange) {
      this.buildPaymentOptions();
      this.handlePaymentTypePredefinedValue();
    }

    this.refreshAdyenAvailability();
  }

  /**
   * Asks whether Adyen can take the money for THIS booking - with the same context the session
   * will be minted with (`buildAdyenBookingContext`), so the backend answers for the store the
   * booking lands in. Re-asked only when that context changes.
   */
  private refreshAdyenAvailability(): void {
    if (!(this.isOnlinePaymentEnabled && this.isAdyenEnabled) || !(this.price > 0)) {
      return;
    }

    const context = buildAdyenBookingContext({
      widgetType: this.widgetType,
      cart: this.cart,
      eventCart: this.eventCart,
      storeId: this.storeId,
      bookingLinkUuid: this.bookingLinkUuid,
      predefinedSlotUuid: this.predefinedSlotUuid,
      isCombiServiceSelected: this.isCombiServiceSelected,
    });
    const key = JSON.stringify(context);
    if (key === this.adyenAvailabilityKey) {
      return;
    }
    this.adyenAvailabilityKey = key;
    this.adyenAvailabilityRequest?.unsubscribe();

    if (!hasAdyenBookingContext(context)) {
      // Nothing names the booking yet, and a session minted now would carry nothing either: the
      // partner-wide answer the parent already applied through `isAdyenEnabled` is the answer.
      this.applyAdyenAvailability(true);
      return;
    }

    this.applyAdyenAvailability(false);
    this.adyenAvailabilityRequest = this.partnerService.verifyOnlinePaymentStatus(context)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: status => this.applyAdyenAvailability(!!status?.adyenEnabled, true),
        error: (error: unknown) => {
          LoggerService.error(error);
          this.applyAdyenAvailability(false, true);
        },
      });
  }

  /**
   * Ask the mounted Adyen Drop-in to pay now. False when there is nothing to submit,
   * so the caller can leave the shopper on the form rather than report a failure.
   */
  submitAdyen(): boolean {
    return this.adyenComponent?.submit() ?? false;
  }

  /**
   * "Complete the payment before you book" is true only while a Drop-in is on screen, waiting for
   * the customer: not while it loads, not behind its own error or result, not while it authorises.
   */
  protected get isAdyenAwaitingPayment(): boolean {
    return !!this.adyenComponent?.dropinMounted() && !this.adyenPaymentInFlight();
  }

  /** The in-flight payment's way out (a 3DS challenge that cannot be finished). */
  protected cancelAdyenPayment(): void {
    this.adyenComponent?.cancelInFlightPayment();
  }

  /** "Pay again" after an unknown booking outcome or a spent session: the customer re-arms the form. */
  protected payAgain(): void {
    this.rearmPaymentOptions();
    this.payAgainEvent.emit();
  }

  /** Brings the payment widgets back into the DOM, so a fresh payment can be authorised. */
  private rearmPaymentOptions(): void {
    this.hidePaymentOptions = false;
    this.showPaymentSuccessMsg = false;
    this.paymentSuccessMsg = '';
    this.showBookingOutcomeUnknown.set(false);
    this.showPaymentSessionSpent.set(false);
    this.showBookingStillCompleting.set(false);
    this.paymentService.payAgainRequired.set(false);
  }

  /**
   * After a paid /book the customer must check their email before paying again. The payment
   * options stay hidden, the banner changes from "paid" to the given "check first" notice, and
   * Book waits for "Pay again" - or, while the booking is still completing or already exists, stays
   * disabled. Without a notice (the booking already exists) none of this component's banners shows.
   */
  private holdForPayAgain(notice?: WritableSignal<boolean>): void {
    this.showPaymentSuccessMsg = false;
    this.showBookingOutcomeUnknown.set(notice === this.showBookingOutcomeUnknown);
    this.showPaymentSessionSpent.set(notice === this.showPaymentSessionSpent);
    this.showBookingStillCompleting.set(notice === this.showBookingStillCompleting);
    this.paymentService.payAgainRequired.set(true);
  }

  private applyAdyenAvailability(available: boolean, answered = false): void {
    this.adyenAvailableForBooking = available;
    this.buildPaymentOptions();
    // A remembered or auto-selected Adyen choice cannot stand once this booking's store says no.
    // While the answer is still pending the choice is kept: Drop-in simply does not mount yet.
    if (answered && !available && this.paymentType === this.paymentTypes.ADYEN) {
      this.onChangePaymentType('');
      return;
    }

    // The availability answer is ASYNC, but the single-method auto-select is not: ngOnInit
    // runs handlePaymentTypePredefinedValue() immediately after kicking this request off,
    // while `adyenAvailableForBooking` is still its provisional `false`. For a partner whose
    // only enabled method is Adyen, the auto-select therefore saw zero selectable methods and
    // left the customer on an empty Payment Type with nothing to choose. Re-running it once
    // the real answer lands is what makes the decision reflect Adyen's actual availability.
    if (answered && available) {
      this.handlePaymentTypePredefinedValue();
    }
  }

  setupTemplates(): void {
    this.templateTitle = this.widgetTemplates.find(template => template.id === 122);
    this.templateTitle && (this.templateTitle.is_multi_language = 1);

    this.templateSubTitle = this.widgetTemplates.find(template => template.id === 123);
    this.templateSubTitle && (this.templateSubTitle.is_multi_language = 1);

    this.inStoreTemplate = this.widgetTemplates.find(template =>
      template.identifier === this.widgetConstants.WIDGET_LABELS.WIDGET_IN_STORE_LABEL
    );
    this.inStoreTemplate && (this.inStoreTemplate.is_multi_language = 1);

    this.paypalTemplate = this.widgetTemplates.find(template =>
      template.identifier === this.widgetConstants.WIDGET_LABELS.WIDGET_PAYPAL_LABEL
    );
    this.paypalTemplate && (this.paypalTemplate.is_multi_language = 1);

    this.saferpayTemplate = this.widgetTemplates.find(template =>
      template.identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_SAFERPAY_LABEL
    );
    this.saferpayTemplate && (this.saferpayTemplate.is_multi_language = 1);

    this.stripeTemplate = this.widgetTemplates.find(template =>
      template.id === this.templateConstant.WIDGET_CREDIT_CARD_LABEL
    );
    this.stripeTemplate && (this.stripeTemplate.is_multi_language = 1);
  }

  onChangePaymentType(paymentType: string): void {
    this.paymentType = paymentType;
    this.showPaymentTypeError = false;
    this.changePaymentTypeEvent.emit(paymentType);
  }

  /** Selection made by the customer, as opposed to a pre-selection we applied. */
  protected onSelectPaymentType(paymentType: string): void {
    this.hasCustomerPickedPaymentType = true;
    // Deliberately does NOT release a held (Pending) Adyen payment. Other methods never consult the
    // hold, so switching to one needs no release; and coming back to Adyen - including after a
    // refresh, where Adyen has to be picked again - must show the Pending result, not a second
    // payable session. Its own "Try again" is the one explicit release.
    this.onChangePaymentType(paymentType);
  }

  onPaypalSuccessfulEvent(event: PayPalOrderDetailModel): void {
    this.showPaymentTypeError = false;
    this.paypalSuccessfulEvent.emit(event);
    this.hidePaymentOptions = true;
    const paypalLabel = this.translate.instant('summary_page_translations.paypal');
    this.paymentSuccessMsg = this.translate.instant('summary_page_translations.payment_success_message', {
      paymentMethod: paypalLabel,
    });
    this.showPaymentSuccessMsg = true;
    this.showPaymentFailedMsg = false;
  }

  /** Drop-in really started a payment: freeze what could change its amount or method. */
  protected onAdyenPaymentStarted(): void {
    this.adyenPaymentInFlight.set(true);
    this.adyenPaymentStartedEvent.emit();
  }

  /** Refused, cancelled or errored: Drop-in says why itself, so no payment message stands beside it. */
  protected onAdyenReset(reason: string | void): void {
    const wasLocked = this.adyenPaymentInFlight();
    this.adyenPaymentInFlight.set(false);
    this.showPaymentTypeError = false;
    this.resetAdyenEvent.emit(reason);
    if (wasLocked) {
      this.returnFocusAfterPaymentLock();
    }
  }

  /**
   * The booking was locked for a payment and is not any more. "Cancel payment" and the disabled pay
   * button that held focus are gone, so focus is put back on a stable control once the unlocked page
   * has rendered (see focusAfterPaymentLock).
   */
  private returnFocusAfterPaymentLock(): void {
    afterNextRender(() => {
      const pane = this.elementRef.nativeElement.querySelector<HTMLElement>('.cw-payment-pane');
      if (pane) {
        focusAfterPaymentLock(pane);
      }
    }, { injector: this.injector });
  }

  protected onResetPaypal(event: unknown): void {
    this.showPaymentTypeError = false;
    this.resetPaypalEvent.emit(event);
  }

  protected onResetSaferpay(reason: string | void): void {
    this.showPaymentTypeError = false;
    this.resetSaferpayEvent.emit(reason);
  }

  /**
   * Adyen authorised. The success banner replaces the payment form so the
   * customer cannot start a second checkout against the same booking — the
   * session is single-use server-side, and a second attempt would only fail.
   */
  onAdyenSuccessfulEvent(paymentDetails: AdyenPaymentDetails): void {
    const wasLocked = this.adyenPaymentInFlight();
    this.adyenPaymentInFlight.set(false);
    this.showPaymentTypeError = false;
    this.adyenSuccessfulEvent.emit(paymentDetails);
    this.hidePaymentOptions = true;
    this.paymentSuccessMsg = this.translate.instant('summary_page_translations.payment_success_message', {
      paymentMethod: this.translate.instant('adyen.payment_method_label'),
    });
    this.showPaymentSuccessMsg = true;
    this.showPaymentFailedMsg = false;
    if (wasLocked) {
      this.returnFocusAfterPaymentLock();
    }
  }

  onSaferpaySuccessfulEvent(paymentDetails: SaferPayPaymentDetailsDbModel): void {
    this.showPaymentTypeError = false;
    this.saferpaySuccessfulEvent.emit(paymentDetails);
    this.hidePaymentOptions = true;
    const paymentMethod = this.titlecasePipe.transform(paymentDetails.payment_method);
    this.paymentSuccessMsg = this.translate.instant('summary_page_translations.payment_success_message', {
      paymentMethod: paymentMethod,
    });
    this.showPaymentSuccessMsg = true;
    this.showPaymentFailedMsg = false;
  }
}
